import { useState } from 'react';
import { Check, X } from 'lucide-react';
import type { QuizQuestion } from '@/types/educate';

interface QuizProps {
  unitId: string;
  index: number;
  question: QuizQuestion;
  onAnswer: (answer: { unitId: string; answer: number; isCorrect: boolean }) => void;
}

export function Quiz({ unitId, index, question, onAnswer }: QuizProps) {
  const [picked, setPicked] = useState<number | null>(null);
  const answerId = `${unitId}-quiz-${index}`;
  const answered = picked !== null;

  const choose = (option: number) => {
    if (answered) return;
    setPicked(option);
    onAnswer({ unitId: answerId, answer: option, isCorrect: option === question.correctIndex });
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-800">
      <p className="font-medium">{question.question}</p>

      <ul className="mt-3 space-y-2">
        {question.options.map((option, optionIndex) => {
          const isPicked = picked === optionIndex;
          const isCorrect = optionIndex === question.correctIndex;
          const tone = !answered
            ? 'border-slate-200 hover:border-brand-400 dark:border-slate-700'
            : isCorrect
              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
              : isPicked
                ? 'border-red-500 bg-red-50 dark:bg-red-950/40'
                : 'border-slate-200 opacity-60 dark:border-slate-700';

          return (
            <li key={option}>
              <button
                type="button"
                disabled={answered}
                onClick={() => choose(optionIndex)}
                className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${tone}`}
              >
                <span className="flex-1">{option}</span>
                {answered && isCorrect && <Check size={16} className="text-emerald-600" />}
                {answered && isPicked && !isCorrect && <X size={16} className="text-red-600" />}
              </button>
            </li>
          );
        })}
      </ul>

      {answered && question.feedback && (
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{question.feedback}</p>
      )}
    </div>
  );
}
